import { Button, Descriptions, Empty, Modal, Tag } from 'antd';
import { BalloonGenerationJob } from '../../interfaces/Ballooning';
import { viewportCappedModalStyles } from '../../utils/modalStyles';

interface GenerationStatsModalProps {
	open: boolean;
	job: BalloonGenerationJob | null | undefined;
	onClose: () => void;
}

const STATUS_COLORS: Record<BalloonGenerationJob['status'], string> = {
	pending: 'default',
	running: 'processing',
	succeeded: 'success',
	failed: 'error',
	cancelled: 'warning',
};

const formatDuration = (seconds: number | null | undefined) => {
	if (seconds === null || seconds === undefined) return '—';
	if (seconds < 60) return `${seconds.toFixed(1)} s`;
	const minutes = Math.floor(seconds / 60);
	return `${minutes} min ${Math.round(seconds - minutes * 60)} s`;
};

const formatTime = (value: string | null | undefined) =>
	value ? new Date(value).toLocaleString() : '—';

const count = (value: number | undefined) => (value ?? 0).toLocaleString();

/** What the latest AI analysis of this drawing cost and took, for review. */
export default function GenerationStatsModal({
	open,
	job,
	onClose,
}: GenerationStatsModalProps) {
	return (
		<Modal
			title="AI analysis stats"
			open={open}
			onCancel={onClose}
			footer={<Button onClick={onClose}>Close</Button>}
			width={560}
			centered
			styles={viewportCappedModalStyles}
		>
			{!job ? (
				<Empty description="This drawing has not been analyzed by AI." />
			) : (
				<Descriptions
					column={1}
					size="small"
					bordered
					labelStyle={{ width: 200 }}
				>
					<Descriptions.Item label="Status">
						<Tag
							color={STATUS_COLORS[job.status]}
							style={{ marginInlineEnd: 0 }}
						>
							{job.status.charAt(0).toUpperCase() + job.status.slice(1)}
						</Tag>
					</Descriptions.Item>
					<Descriptions.Item label="Started by">
						{job.trigger === 'drawing_arrival'
							? 'Automatically, when the drawing arrived'
							: 'A user, from this page'}
					</Descriptions.Item>
					<Descriptions.Item label="Model">
						{job.model_name || '—'}
					</Descriptions.Item>
					<Descriptions.Item label="Analysis time">
						{formatDuration(job.duration_seconds)}
					</Descriptions.Item>
					<Descriptions.Item label="Started">
						{formatTime(job.started_at)}
					</Descriptions.Item>
					<Descriptions.Item label="Finished">
						{formatTime(job.finished_at)}
					</Descriptions.Item>
					<Descriptions.Item label="Pages analyzed">
						{job.pages_done} of {job.pages_total}
					</Descriptions.Item>
					<Descriptions.Item label="Balloons created">
						{job.balloons_created}
					</Descriptions.Item>
					<Descriptions.Item label="Model calls">
						{count(job.gemini_calls)}
					</Descriptions.Item>
					<Descriptions.Item label="Input tokens">
						{count(job.input_tokens)}
					</Descriptions.Item>
					<Descriptions.Item label="Output tokens">
						{count(job.output_tokens)}
						{job.thinking_tokens
							? ` (${count(job.thinking_tokens)} thinking)`
							: ''}
					</Descriptions.Item>
					<Descriptions.Item label="Total tokens">
						<strong>{count(job.total_tokens)}</strong>
					</Descriptions.Item>
					{job.error && (
						<Descriptions.Item label="Error">{job.error}</Descriptions.Item>
					)}
				</Descriptions>
			)}
		</Modal>
	);
}
